import dayjs from "dayjs";

import GroupIcon from "@mui/icons-material/Group";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import SyncIcon from "@mui/icons-material/Sync";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import HistoryIcon from "@mui/icons-material/History";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";

import {
  faAnalytics,
  faConciergeBell,
  faLightbulb
} from "@fortawesome/pro-solid-svg-icons";

import { findProductPrice } from "@aclymatepackages/other-helpers";
import {
  UPDATED_SAAS_MONTHLY_BASE_PRICE,
  UPDATED_SAAS_MONTHLY_TIERED_PRICE,
  UPDATED_SAAS_YEARLY_BASE_PRICE,
  UPDATED_SAAS_YEARLY_TIERED_PRICE,
  UPDATED_CONCIERGE_MONTHLY_BASE_PRICE,
  UPDATED_CONCIERGE_MONTHLY_TIERED_PRICE,
  UPDATED_CONCIERGE_YEARLY_BASE_PRICE,
  UPDATED_CONCIERGE_YEARLY_TIERED_PRICE,
  STARTER_TIER_MONTHLY_PRICE,
  STARTER_TIER_YEARLY_PRICE,
  STARTER_TIER_MAXIMUM_NUMBER_EMPLOYEES
} from "@aclymatepackages/constants";

import Billing from "../../components/platform-pages/settings/Billing";
import Company from "../../components/platform-pages/settings/Company";
import Integrations from "../../components/platform-pages/settings/Integrations";
import Products from "../../components/platform-pages/settings/Products";
import Users from "../../components/platform-pages/settings/Users";
import Vehicles from "../../components/platform-pages/settings/Vehicles";
import Documentation from "../../components/platform-pages/settings/Documentation";
import AccountActivity from "../../components/platform-pages/settings/AccountActivity";

import { useSubscriptionType } from "../hooks/companyData";
import { useAccountData, useCachedDisplayData } from "../firebase";

export const settingsPages = [
  {
    label: "Company Data",
    href: "company",
    icon: <LocationCityIcon />,
    Component: Company
  },
  {
    label: "Account Users",
    href: "users",
    icon: <GroupIcon />,
    Component: Users
  },
  {
    label: "Billing/Invoices",
    href: "billing",
    icon: <CreditCardIcon />,
    Component: Billing
  },
  {
    label: "Products/Services",
    href: "products",
    icon: <ShoppingCartIcon />,
    Component: Products
  },
  {
    label: "Company Vehicles",
    href: "vehicles",
    icon: <DirectionsCarIcon />,
    Component: Vehicles
  },
  {
    label: "Integrations",
    href: "integrations",
    icon: <SyncIcon />,
    Component: Integrations
  },
  {
    label: "Documentation",
    href: "documentation",
    icon: <InsertDriveFileIcon />,
    Component: Documentation
  },
  {
    label: "Account Activity",
    href: "account-activity",
    icon: <HistoryIcon />,
    Component: AccountActivity
  }
];

const EMPLOYEE_THRESHOLD = 500;

export const useEmployeeCount = () => {
  const [employees, employeesLoading] = useCachedDisplayData("employees");
  const [{ employeeCount: dbEmployeeCount }, companyDataLoading] =
    useAccountData();

  const activeEmployees = employees.filter(
    ({ status }) => status !== "terminated"
  );
  const employeeCount = dbEmployeeCount
    ? Number(dbEmployeeCount)
    : activeEmployees.length;

  return [employeeCount, employeesLoading || companyDataLoading];
};

export const useSettingsPages = () => {
  const [employeeCount, employeeCountLoading] = useEmployeeCount();

  if (!employeeCountLoading && employeeCount > EMPLOYEE_THRESHOLD) {
    return settingsPages.filter(({ href }) => href !== "products");
  }

  return settingsPages;
};

export const useSubscriptionProducts = () => {
  const [employees, employeesLoading] = useCachedDisplayData("employees");
  const [{ employeeCount: dbEmployeeCount }, companyDataLoading] =
    useAccountData();
  const [{ type: subscriptionType, status }, subscriptionLoading] =
    useSubscriptionType("saas");

  const activeEmployees = employees.filter(
    ({ status }) => status !== "terminated"
  );
  const employeeCount = dbEmployeeCount
    ? Number(dbEmployeeCount)
    : activeEmployees.length;

  const {
    REACT_APP_SUMMER_2025_STRIPE_PRO_MONTHLY_PRICE_ID,
    REACT_APP_SUMMER_2025_STRIPE_PRO_YEARLY_PRICE_ID,
    REACT_APP_SUMMER_2025_STRIPE_TURNKEY_MONTHLY_PRICE_ID,
    REACT_APP_SUMMER_2025_STRIPE_TURNKEY_YEARLY_PRICE_ID,
    REACT_APP_STRIPE_STARTER_TIER_MONTHLY,
    REACT_APP_STRIPE_STARTER_TIER_YEARLY
  } = process.env;

  const starterTierProductObj =
    (subscriptionType !== "starter" && status === "active") ||
    employeeCount > STARTER_TIER_MAXIMUM_NUMBER_EMPLOYEES
      ? []
      : [
          {
            product: "starter",
            icon: faLightbulb,
            borderColor: "primary",
            title: "Starter",
            image: "analytics-screenshot",
            bullets: [
              "Scopes 1, 2, & 3",
              "Activity-based & spend-based",
              "Offset marketplace access",
              "Unlimited help desk support"
            ],
            prices: [
              {
                interval: "month",
                stripePriceId: REACT_APP_STRIPE_STARTER_TIER_MONTHLY,
                value: STARTER_TIER_MONTHLY_PRICE
              },
              {
                interval: "year",
                stripePriceId: REACT_APP_STRIPE_STARTER_TIER_YEARLY,
                value: STARTER_TIER_YEARLY_PRICE
              }
            ]
          }
        ];

  const subscriptionProducts = [
    ...starterTierProductObj,
    {
      product: "integrations",
      icon: faAnalytics,
      borderColor: "primary",
      title: "Professional",
      image: "analytics-screenshot",
      bullets: [
        "Accounting & utility integrations",
        "Custom climate reporting",
        "One-on-one onboarding assistance",
        "Net Zero Targets"
      ],
      prices: [
        {
          interval: "month",
          stripePriceId: REACT_APP_SUMMER_2025_STRIPE_PRO_MONTHLY_PRICE_ID,
          value: findProductPrice({
            base: UPDATED_SAAS_MONTHLY_BASE_PRICE,
            tiered: UPDATED_SAAS_MONTHLY_TIERED_PRICE,
            employeeCount
          })
        },
        {
          interval: "year",
          stripePriceId: REACT_APP_SUMMER_2025_STRIPE_PRO_YEARLY_PRICE_ID,
          value: findProductPrice({
            base: UPDATED_SAAS_YEARLY_BASE_PRICE,
            tiered: UPDATED_SAAS_YEARLY_TIERED_PRICE,
            employeeCount
          })
        }
      ]
    },
    {
      product: "concierge",
      title: "Turn Key Service",
      icon: faConciergeBell,
      borderColor: "primary",
      image: "concierge",
      bullets: [
        "Data-entry and management done for you",
        "Customized net zero strategy",
        "Custom quarterly reports done for you",
        "Designated Climate Bookeeper"
      ],
      prices: [
        {
          interval: "month",
          stripePriceId: REACT_APP_SUMMER_2025_STRIPE_TURNKEY_MONTHLY_PRICE_ID,
          value: findProductPrice({
            base: UPDATED_CONCIERGE_MONTHLY_BASE_PRICE,
            tiered: UPDATED_CONCIERGE_MONTHLY_TIERED_PRICE,
            employeeCount
          })
        },
        {
          interval: "year",
          stripePriceId: REACT_APP_SUMMER_2025_STRIPE_TURNKEY_YEARLY_PRICE_ID,
          value: findProductPrice({
            base: UPDATED_CONCIERGE_YEARLY_BASE_PRICE,
            tiered: UPDATED_CONCIERGE_YEARLY_TIERED_PRICE,
            employeeCount
          })
        }
      ]
    }
  ];

  const productsWithSubscriptionData = subscriptionProducts.map((productObj) =>
    productObj?.product === subscriptionType
      ? { ...productObj, status }
      : productObj
  );

  return [
    productsWithSubscriptionData,
    employeesLoading || companyDataLoading || subscriptionLoading
  ];
};

export const calculateIsCardExpiringSoon = (expirationDate, expiration) => {
  if (!expirationDate && !expiration) {
    return false;
  }

  if (expirationDate) {
    return Math.abs(dayjs(new Date()).diff(dayjs(expirationDate), "d")) <= 60;
  }

  const [month, year] = expiration.split("/");
  const daysInMonth = dayjs(`${year}-${month}-01`).daysInMonth();
  const newExpirationDate = dayjs(`${month}-${daysInMonth}-${year}`);
  const daysLeft = Math.abs(
    dayjs(new Date()).diff(dayjs(newExpirationDate), "d")
  );

  return daysLeft <= 60;
};
